import { useMemo, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { EmptyState } from '../components/ui'
import { InfoDot } from '../components/InfoDot'
import { pluralise } from '../lib/util'
import { useProjectTotals } from '../lib/team-data'
import { formatMoney, workspaceCurrency } from '../lib/billing'
import { idsOfKind } from '../lib/types'

const FILTERS = ['Active', 'Archived', 'All'] as const
type Filter = (typeof FILTERS)[number]

export function ProjectsScreen() {
  const { ws, act, setModal, setScreen, setDraft, flash, confirm, client, canSeeAll } =
    useWorkspace()
  const { totals } = useProjectTotals(ws.id)
  const currency = workspaceCurrency(ws.rates)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('Active')

  /*
   * A prospect's projects are not listed here.
   *
   * This screen groups by client and rolls hours and money up per account,
   * which is a client view of the work -- putting prospect projects in it
   * would mix the two populations in exactly the place the figures get read
   * from. They are created and picked from the Prospects screen instead.
   */
  const prospectIds = useMemo(() => idsOfKind(ws.clients, 'prospect'), [ws.clients])

  /** Everything this screen could ever show, before the filters narrow it. */
  const pool = useMemo(
    () => ws.projects.filter((p) => !(p.clientId && prospectIds.has(p.clientId))),
    [ws.projects, prospectIds],
  )

  const q = search.trim().toLowerCase()
  const visible = pool
    .filter((p) => (filter === 'All' ? true : filter === 'Active' ? p.status === 'active' : p.status === 'archived'))
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (client(p.clientId)?.name.toLowerCase().includes(q) ?? false))

  /**
   * Projects grouped under the client they belong to.
   *
   * A client owns many projects, and showing the client as a line of small
   * print inside each card buried that. The client is the heading now and its
   * projects sit beneath it, so the shape on screen matches the shape of the
   * work. Clients are alphabetical; "No client" is pinned last rather than
   * sorted into the Ns.
   */
  const grouped = useMemo(() => {
    const byClient = new Map<string, { name: string; projects: typeof visible }>()

    for (const p of visible) {
      const c = client(p.clientId)
      const key = c?.id ?? '~none'
      const entry = byClient.get(key) ?? { name: c?.name ?? 'No client', projects: [] }
      entry.projects.push(p)
      byClient.set(key, entry)
    }

    return [...byClient.entries()]
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => {
        if (a.id === '~none') return 1
        if (b.id === '~none') return -1
        return a.name.localeCompare(b.name)
      })
  }, [visible, client])

  return (
    <section className="screen">
      {/* The totals below come from a view scoped by RLS, so a member sees
          their own hours and an admin sees the team's -- in an identical
          shape, with nothing to tell the two apart. Saying which one this is
          costs a line and stops the figure being read as the project's when
          it is only yours. */}
      {!canSeeAll && (
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 4px' }}>
          Hours shown are your own. Owners and managers see the whole team's.
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input
          className="input"
          style={{ flex: '1 1 200px', maxWidth: 300, padding: '9px 12px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search projects…"
          aria-label="Search projects"
        />
        {FILTERS.map((f) => (
          <button key={f} type="button" className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f}
          </button>
        ))}
        <button type="button" className="btn btn--primary push" onClick={() => setModal({ kind: 'project' })}>
          + New project
        </button>
      </div>

      {!visible.length ? (
        <EmptyState
          title={pool.length ? 'No projects match' : 'No projects yet'}
          body={
            pool.length
              ? 'Nothing here with those filters. Try “All”, or clear the search.'
              : 'Projects give your time a home — a client, a colour, an hourly rate and a budget to track against.'
          }
          action={
            pool.length
              ? { label: 'Show all', onClick: () => { setFilter('All'); setSearch('') } }
              : { label: '+ New project', onClick: () => setModal({ kind: 'project' }) }
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          {grouped.map((group) => (
            <div key={group.id}>
              {/* Client heading. The count is worth showing: it is the
                  answer to "how much is running for them" at a glance. */}
              <div
                className="spread"
                style={{
                  alignItems: 'baseline',
                  gap: 10,
                  paddingBottom: 8,
                  marginBottom: 12,
                  borderBottom: '1px solid var(--line)',
                }}
              >
                <h2 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', margin: 0 }}>
                  {group.name}
                </h2>
                <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                  {pluralise(group.projects.length, 'project')}
                </span>
              </div>

              <div className="card-grid">
              {group.projects.map((p) => {
                const sec = totals.get(p.id)?.seconds ?? 0
                const hours = sec / 3600
                const pct = p.estimate ? Math.min(100, (hours / p.estimate) * 100) : 0
                const over = !!p.estimate && hours > p.estimate

                return (
                  <div key={p.id} className="proj-card">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span className="dot dot--lg" style={{ background: p.color }} aria-hidden />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="trunc" style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-.01em' }}>
                          {p.name}
                        </div>
                      </div>
                      <span className={p.status === 'active' ? 'status status--on' : 'status status--off'}>
                        {p.status === 'active' ? 'Active' : 'Archived'}
                      </span>
                    </div>

                    <div>
                      <div className="spread" style={{ fontSize: 12, color: 'var(--mut)', marginBottom: 6 }}>
                        <span className="mono">
                          {hours.toFixed(1)}h / {p.estimate ? `${p.estimate}h` : '∞'}
                        </span>
                        <span className="mono" style={over ? { color: 'var(--dg)', fontWeight: 600 } : undefined}>
                          {p.estimate ? `${Math.round((hours / p.estimate) * 100)}%` : '—'}
                        </span>
                      </div>
                      <div className="track">
                        <div
                          style={{
                            width: `${Math.max(2, pct)}%`,
                            background: over ? 'var(--dg)' : p.color,
                          }}
                        />
                      </div>
                    </div>

                    <div className="proj-foot">
                      <span>
                        {!p.billable
                          ? 'Non-billable'
                          : p.rate
                            ? `${formatMoney(p.rate, currency)}/h`
                            : 'No rate set'}
                      </span>
                      <span>{p.tasks.length ? pluralise(p.tasks.length, 'task') : 'No tasks'}</span>
                      <span className="push" style={{ display: 'flex', gap: 2 }}>
                        <button
                          type="button"
                          className="ibtn ibtn--wide ibtn--play"
                          title="Track to this project"
                          aria-label={`Track time to ${p.name}`}
                          disabled={p.status !== 'active'}
                          onClick={() => {
                            setDraft({ projectId: p.id, task: '', billable: p.billable })
                            setScreen('timer')
                            flash(`Timer set to ${p.name}`)
                          }}
                        >
                          ▶
                        </button>
                        <InfoDot
                          createdBy={p.createdBy}
                          createdAt={p.createdAt}
                          clientId={p.clientId}
                          what="project"
                        />
                        <>
                          <button
                            type="button"
                            className="ibtn ibtn--wide"
                            title="Edit"
                            aria-label={`Edit ${p.name}`}
                            onClick={() => setModal({ kind: 'project', project: p })}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="ibtn ibtn--wide"
                            title={p.status === 'active' ? 'Archive' : 'Restore'}
                            aria-label={`${p.status === 'active' ? 'Archive' : 'Restore'} ${p.name}`}
                            onClick={() =>
                              void act
                                .updateProject(p.id, { status: p.status === 'active' ? 'archived' : 'active' })
                                .then((ok) => {
                                  if (ok) flash(p.status === 'active' ? `${p.name} archived` : `${p.name} restored`)
                                })
                            }
                          >
                            ⧉
                          </button>
                          <button
                            type="button"
                            className="ibtn ibtn--wide ibtn--del"
                            title="Delete"
                            aria-label={`Delete ${p.name}`}
                            onClick={() =>
                              confirm({
                                title: `Delete ${p.name}?`,
                                body: sec
                                  ? `${hours.toFixed(1)} hours are logged against this project, by everyone in the workspace. The entries are kept but become "No project", so they stop counting towards billable amounts. Archive instead if you only want it out of the way.`
                                  : 'Nothing is logged against it yet, so nothing else changes.',
                                cta: 'Delete project',
                                danger: true,
                                onConfirm: () => void act.deleteProject(p.id),
                              })
                            }
                          >
                            ✕
                          </button>
                        </>
                      </span>
                    </div>
                  </div>
                )
              })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
